import { DatePicker, Button, Tooltip } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useState, useEffect, useRef } from 'react';
import { disabledDate } from 'utils/dateFunctions';

import 'assets/styles/Home/HomeUI/top-date-picker.css';

dayjs.extend(customParseFormat);

const TopDayPicker = ({
    selectedDate,
    isDatePickerDisabled,
    enabledDates,
    updateDate
}) => {

    const prevButtonsRef = useRef(null);
    const nextButtonsRef = useRef(null);

    const findSurroundingDates = (currentDate) => {
        if (enabledDates.length === 0 || !currentDate) return { prevDates: [], nextDates: [] };

        const currentIndex = enabledDates.findIndex(date =>
            dayjs(date).isSame(currentDate, 'day')
        );

        const nextDates = enabledDates.slice(currentIndex + 1, currentIndex + 5).map((date) => dayjs(date));
        while (nextDates.length < 4) nextDates.push(null);

        const prevDatesStart = Math.max(0, currentIndex - 4); // If our start value is less than 0, "slice" returns an empty array.
        const prevDates = enabledDates.slice(prevDatesStart, currentIndex).map((date) => dayjs(date));
        while (prevDates.length < 4) prevDates.unshift(null);

        return { prevDates, nextDates };
    }

    const { prevDates, nextDates } = findSurroundingDates(selectedDate);

    const handleDateButtonClick = (date) => {
        if (!date) return;

        const isGoingLeft = date.isAfter(selectedDate);

        if (isGoingLeft) {
            prevButtonsRef.current.style.animation = 'moveLeftOut 0.15s cubic-bezier(0.33, 1.0, 0.68, 1.0) forwards';
            nextButtonsRef.current.style.animation = 'moveLeftIn 0.15s cubic-bezier(0.22, 1.0, 0.36, 1.0) forwards';
        } else {
            prevButtonsRef.current.style.animation = 'moveRightIn 0.15s cubic-bezier(0.22, 1.0, 0.36, 1.0) forwards';
            nextButtonsRef.current.style.animation = 'moveRightOut 0.15s cubic-bezier(0.33, 1.0, 0.68, 1.0) forwards';
        };

        setTimeout(() => {
            prevButtonsRef.current.style.animation = '';
            nextButtonsRef.current.style.animation = '';

            updateDate(date);
        }, 200);
    };

    const renderPrevButtons = (prevDates) => {
        if (!prevDates.length) return null;

        return prevDates.map((date, index) => { // Dates can be disabled (null) or enabled.
            return date ? (
                <div
                    key={`prev-${index}`}
                    style={{ position: 'relative' }}
                >
                    <Button
                        onClick={() => handleDateButtonClick(date)}
                        className={`date-button ${index === 0 ? 'first-button' : ''}`}
                    >
                        {index === 0 ? <i className="fa-solid fa-caret-left" /> : date.format('DD/MM/YYYY')}
                    </Button>
                </div>
            ) : (
                <div
                    key={`prev-${index}`}
                    style={{ position: 'relative' }}
                >
                    <Button disabled={true} className={`date-button ${index === 0 ? 'first-button' : ''}`}>
                        {index === 0 ? <i className="fa-solid fa-caret-left" /> : selectedDate.subtract(prevDates.length - index, 'day').format('DD/MM/YYYY')}
                    </Button>
                </div>
            );
        });
    };

    const renderNextButtons = (nextDates) => {
        if (!nextDates.length) return null;

        return nextDates.map((date, index) => { // Dates can be disabled (null) or enabled.
            return date ? (
                <div
                    key={`next-${index}`}
                    style={{ position: 'relative' }}
                >
                    <Button
                        onClick={() => handleDateButtonClick(date)}
                        className={`date-button ${index === nextDates.length - 1 ? 'last-button' : ''}`}
                    >
                        {index === nextDates.length - 1 ? <i className="fa-solid fa-caret-right" /> : date.format('DD/MM/YYYY')}
                    </Button>
                </div>
            ) : (
                <div
                    key={`next-${index}`}
                    style={{ position: 'relative' }}
                >
                    <Button disabled={true} className={`date-button ${index === nextDates.length - 1 ? 'last-button' : ''}`}>
                        {index === nextDates.length - 1 ? <i className="fa-solid fa-caret-right" /> : selectedDate.add(index + 1, 'day').format('DD/MM/YYYY')}
                    </Button>
                </div>
            );
        });
    };

    if (isDatePickerDisabled) {
        return null;
    };

    return (
        <div className="date-picker-container">

            <div
                className="date-buttons-container prev-dates"
                ref={prevButtonsRef}
            >
                { renderPrevButtons(prevDates) }
            </div>

            <Tooltip mouseLeaveDelay={"0.02s"} id='ant-picker-tooltip' align="center" placement="top" title="Open calendar">
                <DatePicker
                    value={selectedDate}
                    style={{ zIndex: 9999, margin: '0px' }}
                    format={'DD/MM/YYYY'}
                    onChange={updateDate}
                    disabledDate={(current) => disabledDate(enabledDates, current)}
                    disabled={isDatePickerDisabled}
                    allowClear={false}
                    inputReadOnly={false}
                />
            </Tooltip>

            <div
                className="date-buttons-container next-dates"
                ref={nextButtonsRef}
            >
                { renderNextButtons(nextDates) }
            </div>

        </div>
    );
}

export default TopDayPicker;